import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Zap, Users, Check } from "lucide-react";
import axios from "axios";
import MSABanner from "../components/MSABanner";
import DocumentsSection from "../components/DocumentsSection";
import { LevelBadge, levelLabel, levelRank, FEATURED_LEVEL_IDS } from "../components/SponsorLevels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ANY_AMOUNT_URL = "https://buy.stripe.com/3cI3cu68Od6ngA4dm3b3q07";

const TIERS = [
    {
        id: "pyramid-peak",
        label: "Pyramid Peak Partner",
        amount: "$25,000+",
        benefits: [
            "Everything in Crescent Peak Partner",
            "Official grand opening sponsor",
            "Mentioned in the official grand opening press release",
            "Prominent park entrance legacy signage",
            "Prominent legacy signage throughout Seeley Lake Skate Park",
        ],
    },
    {
        id: "crescent-peak",
        label: "Crescent Peak Partner",
        amount: "$15,000–$24,999",
        benefits: [
            "Everything in Devine Peak Partner",
            "Physical park legacy signage",
        ],
    },
    {
        id: "devine-peak",
        label: "Devine Peak Partner",
        amount: "$5,000–$14,999",
        benefits: [
            "Everything in Marshall Mountain Partner",
            "Prominent logo on website",
            "Event recognition opportunities",
        ],
    },
    {
        id: "marshall-mountain",
        label: "Marshall Mountain Partner",
        amount: "$2,500–$4,999",
        benefits: [
            "Everything in Rice Ridge Partner",
            "Logo on website",
        ],
    },
    {
        id: "rice-ridge",
        label: "Rice Ridge Partner",
        amount: "$500–$2,499",
        benefits: [
            "Everything in Big Gus Partner",
            "Hat",
            "Acknowledgement letter",
        ],
    },
    {
        id: "big-gus",
        label: "Big Gus Partner",
        amount: "$250–$499",
        benefits: [
            "Names Club listing (website + in the park)",
            "Donation thank-you letter (includes gift amount and nonprofit number)",
            "Walk of fame brick",
            "Sponsor t-shirt",
            "Sticker",
        ],
    },
];

const Sponsors = () => {
    const [sponsors, setSponsors] = useState([]);

    useEffect(() => {
        axios.get(`${API}/sponsors`).then(r => setSponsors(r.data)).catch(() => {});
    }, []);

    // Featured levels (Pyramid Peak, Crescent Peak) get the big cards; if none exist, the highest level present is featured.
    const presentLevels = [...new Set(sponsors.map(s => s.tier))].sort((a, b) => levelRank(a) - levelRank(b));
    const featuredIds = presentLevels.some(t => FEATURED_LEVEL_IDS.includes(t)) ? FEATURED_LEVEL_IDS : presentLevels.slice(0, 1);
    const majorSponsors = sponsors.filter(s => featuredIds.includes(s.tier)).sort((a, b) => levelRank(a.tier) - levelRank(b.tier));
    const communitySponsors = sponsors.filter(s => !featuredIds.includes(s.tier));
    const communityGroups = presentLevels.filter(t => !featuredIds.includes(t))
        .map(t => ({ tier: t, label: levelLabel(t) + "s", items: communitySponsors.filter(s => s.tier === t) }))
        .filter(g => g.items.length > 0);
    const logoSrc = (url) => url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

    return (
        <div className="pt-16">
            {/* Hero */}
            <section className="py-20 px-4 bg-[#3D2008]" data-testid="sponsors-hero">
                <div className="max-w-7xl mx-auto">
                    <span className="font-marker text-[#C9A84C] text-2xl -rotate-1 inline-block mb-4">Partners in Progress</span>
                    <h1 className="font-anton text-5xl sm:text-7xl lg:text-8xl text-[#F5EDD6] uppercase leading-none mb-4">Sponsors</h1>
                    <p className="font-manrope text-[#F5EDD6]/70 text-xl max-w-2xl">
                        Our sponsors make this park possible. Every level of support moves us closer to breaking ground.
                    </p>
                </div>
            </section>

            {/* MSA Partner Banner — above Major Sponsors */}
            <MSABanner label="Our Partner" />

            {/* Major Sponsors (Seeley Electric, Exact Image, etc.) */}
            {majorSponsors.length > 0 && (
                <section className="py-16 px-4 bg-[#2D5A27]" data-testid="major-sponsors-section">
                    <div className="max-w-7xl mx-auto">
                        <div className="mb-10">
                            <span className="font-marker text-[#C9A84C] text-2xl -rotate-1 inline-block mb-2">Local Champions</span>
                            <h2 className="font-anton text-4xl sm:text-5xl text-[#F5EDD6] uppercase">Featured Partners</h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {majorSponsors.map((s, i) => (
                                <div key={s.id || i} data-testid={`major-sponsor-${i}`}
                                    className="bg-[#F5EDD6] border-4 border-[#C9A84C] p-8 shadow-[6px_6px_0px_0px_rgba(201,168,76,0.4)]">
                                    <div className="flex items-center gap-3 mb-3">
                                        <Zap size={20} className="text-[#C4522A]" />
                                        <LevelBadge level={s.tier} large testId={`major-sponsor-${i}-level`} />
                                    </div>
                                    {s.logo_url && <img src={logoSrc(s.logo_url)} alt={s.name} className="h-16 object-contain mb-3" />}
                                    <h3 className="font-anton text-3xl text-[#3D2008] uppercase mb-2">{s.name}</h3>
                                    <p className="font-manrope text-[#5C4A35] leading-relaxed">{s.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Every other sponsor, grouped by tier */}
            {communitySponsors.length > 0 && (
                <section className="py-16 px-4 bg-white border-b-2 border-[#3D2008]" data-testid="community-sponsors-section">
                    <div className="max-w-7xl mx-auto">
                        <div className="mb-10">
                            <span className="font-marker text-[#C4522A] text-2xl -rotate-1 inline-block mb-2">Thank You</span>
                            <h2 className="font-anton text-4xl sm:text-5xl text-[#3D2008] uppercase">Community Sponsors</h2>
                            <p className="font-manrope text-[#5C4A35] mt-3 max-w-2xl">
                                Local businesses, families and neighbors who have stepped up to help build the park. Every one of them matters.
                            </p>
                        </div>
                        <div className="space-y-12">
                            {communityGroups.map(group => (
                                <div key={group.tier} data-testid={`sponsor-group-${group.tier}`}>
                                    <div className="flex items-center gap-4 mb-5">
                                        <h3 className="font-anton text-xl sm:text-2xl text-[#2D5A27] uppercase tracking-wide">{group.label}</h3>
                                        <div className="flex-1 h-0.5 bg-[#C9A84C]/60" />
                                        <span className="font-manrope text-xs font-bold text-[#5C4A35] uppercase tracking-widest">{group.items.length}</span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                        {group.items.map((s, i) => (
                                            <div key={s.id || `${group.tier}-${i}`} data-testid={`community-sponsor-${group.tier}-${i}`}
                                                className="bg-[#F5EDD6] border-2 border-[#3D2008] p-5 shadow-[4px_4px_0px_0px_rgba(61,32,8,0.2)] hover:-translate-y-0.5 transition-transform duration-200 flex flex-col">
                                                {s.logo_url && <img src={logoSrc(s.logo_url)} alt={s.name} className="h-12 w-auto object-contain self-start mb-3" />}
                                                <h4 className="font-anton text-xl text-[#3D2008] uppercase leading-tight">{s.name}</h4>
                                                <div className="mt-2"><LevelBadge level={s.tier} testId={`community-sponsor-${group.tier}-${i}-level`} /></div>
                                                {s.description && <p className="font-manrope text-sm text-[#5C4A35] mt-2 leading-relaxed">{s.description}</p>}
                                                {s.website && (
                                                    <a href={s.website.startsWith("http") ? s.website : `https://${s.website}`} target="_blank" rel="noopener noreferrer"
                                                        className="mt-auto pt-3 font-manrope text-xs font-bold text-[#C4522A] uppercase tracking-wide hover:text-[#3D2008] transition-colors">
                                                        Visit website →
                                                    </a>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Documents & downloads (sponsorship packet, 501c3, receipts) */}
            <DocumentsSection />

            {/* The Bob Partner — Title Sponsor */}
            <section className="py-16 px-4 bg-[#3D2008]" data-testid="the-bob-section">                <div className="max-w-7xl mx-auto">
                    <div className="border-2 border-[#C9A84C] p-8 sm:p-12">
                        <div className="flex flex-col lg:flex-row lg:items-start gap-8">
                            <div className="flex-1">
                                <span className="inline-block bg-[#C9A84C] text-[#3D2008] font-manrope text-[11px] font-bold uppercase tracking-[0.14em] px-3 py-1 mb-5">
                                    Title Partner
                                </span>
                                <h3 className="font-anton text-3xl sm:text-4xl text-[#F5EDD6] uppercase mb-3">The Bob Partner</h3>
                                <p className="font-manrope text-[#F5EDD6]/75 text-base leading-relaxed max-w-xl mb-5">
                                    Named for the Bob Marshall Wilderness — the wild country that defines this corner of Montana. As The Bob Partner, your name becomes part of the park's identity permanently. This is a naming-rights partnership that requires a direct conversation.
                                </p>
                                <ul className="space-y-2">
                                    {["Permanent naming recognition on park entrance signage","Top billing on all SLSA materials, website, and social media","Featured at all fundraising events and the grand opening","Enduring legacy in the Seeley Lake community"].map(b => (
                                        <li key={b} className="flex items-start gap-2.5 font-manrope text-sm text-[#F5EDD6]/65">
                                            <Check size={13} className="text-[#C9A84C] mt-0.5 flex-shrink-0 stroke-[2.5]" />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="lg:flex-shrink-0 lg:pt-1">
                                <a href="mailto:info@seeleyskatepark.org"
                                    data-testid="title-sponsor-cta"
                                    className="inline-block whitespace-nowrap py-3 px-6 border-2 border-[#C9A84C] text-[#C9A84C] font-manrope font-semibold text-sm hover:bg-[#C9A84C] hover:text-[#3D2008] transition-colors duration-200">
                                    Contact Us to Become a Title Sponsor
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Names Club */}
            <section className="py-16 px-4 bg-[#C4522A]" data-testid="names-club-section">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <span className="font-marker text-white/80 text-2xl -rotate-1 inline-block mb-4">For Everyone</span>
                        <h2 className="font-anton text-5xl sm:text-6xl text-white uppercase leading-none mb-4">The Names Club</h2>
                        <p className="font-manrope text-white/90 text-xl leading-relaxed mb-4">
                            Give <strong>$250 or more</strong> and your name will be listed on our website and permanently displayed in the park when it's built.
                        </p>
                        <p className="font-manrope text-white/70 text-lg leading-relaxed mb-8">
                            Every name on that wall tells the story of the community that built this park.
                        </p>
                        <a href={ANY_AMOUNT_URL} target="_blank" rel="noopener noreferrer"
                            data-testid="names-club-donate-btn"
                            className="inline-block px-8 py-4 bg-white text-[#C4522A] font-anton uppercase tracking-wider text-lg border-2 border-[#3D2008] shadow-[4px_4px_0px_0px_#3D2008] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform duration-150">
                            Join the Names Club — Donate $250+
                        </a>
                    </div>
                    <div className="bg-[#3D2008] border-2 border-[#C9A84C] p-8 shadow-[8px_8px_0px_0px_rgba(201,168,76,0.3)]">
                        <div className="flex items-center gap-3 mb-6">
                            <Users size={24} className="text-[#C9A84C]" />
                            <h3 className="font-anton text-[#C9A84C] uppercase text-xl">Names Club Members</h3>
                        </div>
                        <p className="font-manrope text-[#F5EDD6]/60 text-sm mb-4">
                            Every gift of $250 or more — including every Big Gus Partner and above — earns a listing here and in the park.
                        </p>
                        <div className="border-t border-[#C9A84C]/30 pt-4">
                            <p className="font-marker text-[#C9A84C] text-lg">Be the first on the wall.</p>
                            <p className="font-manrope text-[#F5EDD6]/50 text-sm mt-1">Names added upon donation confirmation.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Sponsorship Tiers */}
            <section className="py-20 px-4 bg-[#F5EDD6]" data-testid="sponsor-tiers-section">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-14">
                        <span className="font-marker text-[#C4522A] text-2xl -rotate-1 inline-block mb-4">Partner With Us</span>
                        <h2 className="font-anton text-4xl sm:text-6xl text-[#3D2008] uppercase">Sponsorship Tiers</h2>
                        <p className="font-manrope text-[#5C4A35] text-base mt-4 max-w-2xl mx-auto">
                            Each tier includes everything from the level below it. Tiers show the range — donate any amount within your chosen tier.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {TIERS.map((tier) => (
                            <div key={tier.id} data-testid={`sponsor-tier-${tier.id}`}
                                className="bg-white border border-[#D5D0C7] hover:border-[#3D2008] hover:shadow-md transition-all duration-200 p-6 flex flex-col">
                                <h3 className="font-anton text-xl text-[#3D2008] uppercase leading-tight mb-1">{tier.label}</h3>
                                <p className="font-manrope font-bold text-2xl text-[#C4522A] mb-5">{tier.amount}</p>
                                <ul className="space-y-2 flex-1 mb-6">
                                    {tier.benefits.map(b => (
                                        <li key={b} className="flex items-start gap-2 text-sm font-manrope text-[#3D2008]">
                                            <Check size={13} className="text-[#2D5A27] mt-0.5 flex-shrink-0" />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                                <a href={ANY_AMOUNT_URL} target="_blank" rel="noopener noreferrer"
                                    data-testid={`sponsor-cta-${tier.id}`}
                                    className="block text-center py-2.5 px-4 border-2 border-[#3D2008] text-[#3D2008] font-manrope font-semibold text-sm hover:bg-[#3D2008] hover:text-white transition-colors duration-200">
                                    Become a Sponsor
                                </a>
                                <p className="font-manrope text-xs text-[#8A7A65] mt-2 text-center">Enter your amount on the next page</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 px-4 bg-[#3D2008]">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="font-anton text-4xl sm:text-5xl text-[#F5EDD6] uppercase mb-4">Ready to Partner With Us?</h2>
                    <p className="font-manrope text-[#F5EDD6]/60 text-lg mb-8">
                        Questions about sponsorship, matching gifts, or in-kind support? Reach out directly.
                    </p>
                    <a href="mailto:info@seeleyskatepark.org" data-testid="sponsors-contact-link"
                        className="inline-block px-8 py-4 bg-[#C4522A] text-white font-anton uppercase tracking-wider text-lg border-2 border-[#C9A84C] shadow-[4px_4px_0px_0px_rgba(201,168,76,0.5)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform duration-150">
                        Email Us
                    </a>
                </div>
            </section>
        </div>
    );
};

export default Sponsors;
